import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Pull-to-refresh for mobile. Returns { pulling, progress, isRefreshing }.
 * Attaches passive touch listeners to window and triggers `onRefresh` when the
 * user pulls more than `threshold` pixels from the top of the page.
 */
export default function usePullToRefresh(onRefresh, { threshold = 70, maxPull = 120, enabled = true } = {}) {
  const startY = useRef(null);
  const pullingRef = useRef(false);
  const [pullY, setPullY] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const trigger = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } catch (err) {
      console.warn("Pull-to-refresh handler threw", err);
    }
    setIsRefreshing(false);
    setPullY(0);
  }, [onRefresh, isRefreshing]);

  useEffect(() => {
    if (!enabled) return;
    const onTouchStart = (e) => {
      if (window.scrollY > 0) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
      pullingRef.current = false;
    };
    const onTouchMove = (e) => {
      if (startY.current == null || isRefreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { setPullY(0); return; }
      if (window.scrollY > 0) { setPullY(0); return; }
      pullingRef.current = true;
      // Dampen (resistance curve)
      const damped = Math.min(maxPull, Math.pow(dy, 0.85));
      setPullY(damped);
    };
    const onTouchEnd = () => {
      if (!pullingRef.current) { setPullY(0); return; }
      if (pullY >= threshold) {
        trigger();
      } else {
        setPullY(0);
      }
      startY.current = null;
      pullingRef.current = false;
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, isRefreshing, maxPull, pullY, threshold, trigger]);

  const progress = Math.min(1, pullY / threshold);
  return { pullY, progress, isRefreshing, trigger };
}
